import { DisplayMagnitude } from '@prisma/client';
import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { useQuotePdfContext } from '@ui/pages/QuoteBuilder/hooks';
import type { QuotePdfTemplateConfig } from '@ui/pdfs/customer-quote/config';
import { styles } from '@ui/pdfs/styles';

import { formatPriceBreaks } from '../utilities/format-price-breaks';
import { EstimatedQuantityOnHand } from './EstQtyOnHand';
import { PdfLineItemDetails } from './LineItemDetails';
import { PartId } from './PartId';

type PdfLineItemProps = {
  lineIndex: number;
  config: QuotePdfTemplateConfig;
};

const pdfLineItemStyles = StyleSheet.create({
  wrapper: {
    borderTopWidth: '1px',
    borderTopColor: '#000000',
    borderTopStyle: 'dashed',
    padding: 10,
  },
  leftColumnWrapper: {
    minWidth: '33%',
    alignItems: 'flex-end',
    gap: 36,
    justifyContent: 'space-between',
  },
});

export function PdfLineItem({ lineIndex, config }: PdfLineItemProps) {
  const quoteContext = useQuotePdfContext();
  const lineItem = quoteContext.getLineForm(lineIndex);
  const quantityDisplayMagnitude =
    quoteContext.currentSite.siteConfig.quotingConfig.quantityDisplayMagnitude;
  const {
    description,
    leadTime,
    lineNumber,
    note,
    drawing,
    drawingRevision,
    partOptions,
    customerPartId,
  } = lineItem;

  const priceBreaks = formatPriceBreaks(
    lineItem,
    quantityDisplayMagnitude,
    quoteContext.currentSite.code,
  );

  const pricingUnit =
    quantityDisplayMagnitude === DisplayMagnitude.ONES
      ? 'Price Each'
      : 'Price/M';

  return (
    <View style={[styles.gap10, styles.column, pdfLineItemStyles.wrapper]}>
      <View style={[styles.gap10, styles.row]}>
        <View style={styles.column}>
          <Text>Line Number {lineNumber.toString().padStart(2, '0')}</Text>
        </View>

        <View
          style={[
            styles.gap10,
            styles.column,
            { flex: 1, justifyContent: 'space-between' },
          ]}
        >
          <View>
            <PartId
              lineItem={lineItem}
              siteCode={quoteContext.currentSite.code}
            />

            {config.showCustomerPartId && customerPartId && (
              <Text style={{ paddingBottom: '4px' }}>
                Customer Part: <Text style={styles.bold}>{customerPartId}</Text>
              </Text>
            )}

            {config.showPartDescription && <Text>{description}</Text>}

            {partOptions?.map((option) => {
              if (option.name === 'None') {
                return null;
              }

              return (
                <Text
                  key={option.id}
                >{`${option.groupName}: ${option.name}`}</Text>
              );
            })}
          </View>

          {note && <Text style={styles.bold}>{note}</Text>}

          {config.showQuantityOnHand && (
            <EstimatedQuantityOnHand lineIndex={lineIndex} />
          )}
        </View>

        <View style={[styles.column, pdfLineItemStyles.leftColumnWrapper]}>
          <PdfLineItemDetails
            drawing={drawing}
            drawingRevision={drawingRevision}
            leadTime={leadTime}
            config={config}
          />

          <View
            style={[
              styles.gap10,
              styles.row,
              styles.widthFull,
              { justifyContent: 'space-between' },
            ]}
          >
            <View style={[styles.alignEnd, styles.column, styles.gap4]}>
              <Text style={styles.underline}>Quantity</Text>
              {priceBreaks.map((priceBreak) => (
                <Text key={`${lineNumber}-qty-${priceBreak.quantity}`}>
                  {priceBreak.quantity}
                </Text>
              ))}
            </View>
            <View style={[styles.alignEnd, styles.column, styles.gap4]}>
              <Text style={styles.underline}>{pricingUnit}</Text>
              {priceBreaks.map((priceBreak) => (
                <Text key={`${lineNumber}-price-${priceBreak.price}`}>
                  {priceBreak.price}
                </Text>
              ))}
            </View>
            <View style={[styles.alignEnd, styles.column, styles.gap4]}>
              <Text style={styles.underline}>Line Total</Text>
              {priceBreaks.map((priceBreak) => (
                <Text key={`${lineNumber}-total-${priceBreak.total}`}>
                  {priceBreak.total}
                </Text>
              ))}
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}
